import React, { useEffect, useState } from 'react';
import { adminApi } from '../../lib/api';
import { Link } from 'react-router-dom';
import { Package, MessageSquare, FileText, Wallet, Mail, Phone, TrendingUp, Activity, Users, MailX } from 'lucide-react';

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    adminApi.stats().then(setStats).catch(() => setStats({}));
  }, []);

  const cards = [
    { label: 'Active Offers', value: stats?.offers_active ?? '–', sub: `${stats?.offers ?? 0} total`, icon: Package, color: 'emerald', to: '/admin/offers' },
    { label: 'Active Popups', value: stats?.popups_active ?? '–', sub: `${stats?.popups ?? 0} total`, icon: MessageSquare, color: 'amber', to: '/admin/popups' },
    { label: 'Total Submissions', value: stats?.submissions_total ?? '–', sub: 'across all forms', icon: FileText, color: 'sky', to: '/admin/submissions' },
    { label: 'Loan Applications', value: stats?.submissions_loan ?? '–', sub: 'from Loan page', icon: Wallet, color: 'emerald', to: '/admin/submissions?type=loan' },
    { label: 'Newsletter Signups', value: stats?.submissions_newsletter ?? '–', sub: 'Prime List', icon: Mail, color: 'purple', to: '/admin/submissions?type=newsletter' },
    { label: 'Contact / Other', value: stats?.submissions_contact ?? '–', sub: 'inquiries', icon: Phone, color: 'rose', to: '/admin/submissions?type=contact' },
    { label: 'Unsubscribes', value: stats?.submissions_unsubscribe ?? '–', sub: 'email opt-outs', icon: MailX, color: 'rose', to: '/admin/submissions?type=unsubscribe' },
    { label: 'Total Pageviews', value: stats?.pageviews_total ?? '–', sub: 'across all sessions', icon: Activity, color: 'amber', to: '/admin/analytics' },
    { label: 'Unique Sessions', value: stats?.sessions_total ?? '–', sub: 'distinct visitors', icon: Users, color: 'sky', to: '/admin/analytics' },
  ];

  const colorMap = {
    emerald: 'from-emerald-500 to-teal-600',
    amber: 'from-amber-500 to-orange-500',
    sky: 'from-sky-500 to-blue-600',
    purple: 'from-violet-500 to-purple-600',
    rose: 'from-rose-500 to-pink-600',
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-serif-display text-3xl font-bold text-slate-900">Dashboard</h1>
        <p className="text-slate-600 mt-1">Manage offers, popups, and form submissions from one place.</p>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {cards.map((c) => (
          <Link key={c.label} to={c.to} className="group rounded-xl bg-white border border-slate-200 p-5 hover:border-emerald-300 hover:shadow-md transition-all">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{c.label}</div>
                <div className="mt-2 font-serif-display text-3xl font-bold text-slate-900">{c.value}</div>
                <div className="text-xs text-slate-500 mt-1">{c.sub}</div>
              </div>
              <div className={`w-11 h-11 rounded-lg bg-gradient-to-br ${colorMap[c.color]} flex items-center justify-center text-white shadow`}>
                <c.icon className="w-5 h-5" />
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-8 grid md:grid-cols-2 gap-4">
        <Link to="/admin/offers" className="rounded-xl bg-white border border-slate-200 p-6 hover:shadow-md transition-all">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
              <Package className="w-5 h-5 text-emerald-700" />
            </div>
            <div className="font-semibold text-slate-900">Manage Offers</div>
          </div>
          <p className="mt-3 text-sm text-slate-600">Add, edit, reorder or deactivate offers on Protect Credit and Financials pages. Upload banners, set links.</p>
        </Link>
        <Link to="/admin/popups" className="rounded-xl bg-white border border-slate-200 p-6 hover:shadow-md transition-all">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-100 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-amber-700" />
            </div>
            <div className="font-semibold text-slate-900">Manage Popups</div>
          </div>
          <p className="mt-3 text-sm text-slate-600">Configure page-specific popups with image, title, description, and clickable CTA.</p>
        </Link>
      </div>
    </div>
  );
}
